import { MAX_CADENCE, type RunKey } from '@decathlon/shared';
import { clear, h, keyLabel } from '../dom.js';
import type { KeyBinding } from '../controls.js';

export interface HudModel {
  /** Big left readout: time for track, distance/height for field. */
  primary: string;
  primaryLabel: string;
  secondary: string;
  secondaryLabel: string;
  speed: number;
  cadence: number;
  sweetCadence: number;
  stamina: number | null;
  /** The key that will count next (the other one was pressed last). */
  nextKey: RunKey | -1;
  /** Angle gauge (degrees) while Space is held, or null to hide. */
  gauge: number | null;
  gaugeIdeal: number;
  /** Attempt markers for field events. */
  attempts: { label: string; state: 'ok' | 'foul' | 'pending' | 'current' }[] | null;
}

/** One line of the athletes board (positions in a race, marks in a field event). */
export interface HudBoardRow {
  id: string;
  name: string;
  kit: string;
  me: boolean;
  value: string;
  sort: number;
}

export interface HudOptions {
  title: string;
  subtitle: string;
  binding: KeyBinding;
  viewName: string;
  musicName: string;
  sfxOn: boolean;
  onView(): void;
  onMusic(): void;
  onSfx(): void;
  onQuit(): void;
  onRun?(key: RunKey): void;
  onAction?(down: boolean): void;
  onHeight?(height: number): void;
  /** Field events: skip the bot whose turn it is. */
  onSkip?(): void;
}

export class Hud {
  private root: HTMLElement;
  private skipBtn = h(
    'button',
    {
      class: 'btn small blue',
      hidden: true,
      title: 'Skip watching this bot (Enter)',
      onclick: () => this.opts.onSkip?.(),
    },
    '⏭ Skip bot',
  );

  /** Show the skip button while a bot is taking its turn. */
  setSkippable(on: boolean): void {
    this.skipBtn.hidden = !on;
  }
  private primaryEl = h('div', { class: 'v mono' }, '0.00');
  private primaryLabel = h('div', { class: 'l' }, 'Time');
  private secondaryEl = h('div', { class: 'v mono' }, '0 m');
  private secondaryLabel = h('div', { class: 'l' }, 'Distance');
  private speedEl = h('div', { class: 'v mono' }, '0.0');
  private cadEl = h('div', { class: 'v mono' }, '0.0');
  private meterFill = h('div', { class: 'meter-fill' });
  private meterBand = h('div', { class: 'meter-band' });
  private staminaWrap = h('div', { class: 'stamina', hidden: true });
  private staminaFill = h('div', { class: 'stamina-fill' });
  private attemptsEl = h('div', { class: 'attempts', hidden: true });
  private gaugeWrap = h('div', { class: 'gauge', hidden: true });
  private gaugeFill = h('div', { class: 'gauge-fill' });
  private gaugeBand = h('div', { class: 'gauge-band' });
  private gaugeText = h('div', { class: 'gauge-text mono' }, '0°');
  private boardEl = h('div', { class: 'athletes-board', hidden: true });
  private bannerEl = h('div', { class: 'hud-banner', hidden: true });
  private boardRows = new Map<string, { row: HTMLElement; value: HTMLElement }>();
  private keyL: HTMLElement;
  private keyR: HTMLElement;
  private keySpace = h('kbd', { class: 'runkey space' }, 'Space');
  private keyHint = h('div', { class: 'key-hint' }, 'Alternate!');
  private viewBtn: HTMLElement;
  private musicBtn: HTMLElement;
  private sfxBtn: HTMLElement;
  private msgEl: HTMLElement | null = null;
  private nextHeight = 0;
  private heightButton = h('button', { class: 'btn small ghost', hidden: true }, 'Raise bar');
  private lastAttempts = '';

  constructor(
    parent: HTMLElement,
    private opts: HudOptions,
    showSpace = false,
  ) {
    this.heightButton.addEventListener('click', () => opts.onHeight?.(this.nextHeight));
    this.keyL = h('kbd', { class: 'runkey' }, keyLabel(opts.binding.left));
    this.keyR = h('kbd', { class: 'runkey' }, keyLabel(opts.binding.right));
    this.viewBtn = h(
      'button',
      { class: 'btn small ghost', title: 'Change camera', onclick: () => opts.onView() },
      `🎥 ${opts.viewName}`,
    );
    this.musicBtn = h(
      'button',
      { class: 'btn small ghost', title: 'Music on/off', onclick: () => opts.onMusic() },
      `🎵 ${opts.musicName}`,
    );
    this.sfxBtn = h(
      'button',
      { class: 'btn small ghost', title: 'Sound effects on/off', onclick: () => opts.onSfx() },
      opts.sfxOn ? '🔊 On' : '🔇 Off',
    );
    this.staminaWrap.append(
      h('div', { class: 'l' }, 'Stamina'),
      h('div', { class: 'stamina-track' }, this.staminaFill),
    );
    this.gaugeWrap.append(
      h('div', { class: 'gauge-track' }, this.gaugeBand, this.gaugeFill),
      this.gaugeText,
      h('div', { class: 'l' }, 'Angle'),
    );
    this.root = h(
      'div',
      { class: 'hud' },
      h(
        'div',
        { class: 'hud-top' },
        h(
          'div',
          { class: 'stat title' },
          h('div', { class: 'v' }, opts.title),
          h('div', { class: 'l' }, opts.subtitle),
        ),
        h('div', { class: 'stat' }, this.primaryEl, this.primaryLabel),
        h('div', { class: 'stat' }, this.secondaryEl, this.secondaryLabel),
        h('div', { class: 'stat' }, this.speedEl, h('div', { class: 'l' }, 'm/s')),
        h(
          'div',
          { class: 'stat cadence' },
          this.cadEl,
          h('div', { class: 'meter' }, this.meterBand, this.meterFill),
          h('div', { class: 'l' }, 'Presses / s'),
        ),
        this.staminaWrap,
        this.attemptsEl,
      ),
      h(
        'div',
        { class: 'hud-tools' },
        this.viewBtn,
        this.musicBtn,
        this.sfxBtn,
        this.skipBtn,
        h(
          'button',
          { class: 'btn small red', title: 'Quit (Esc)', onclick: () => opts.onQuit() },
          '✕ Quit',
        ),
      ),
      this.boardEl,
      this.bannerEl,
      this.gaugeWrap,
      h(
        'div',
        { class: 'hud-bottom' },
        h(
          'div',
          { class: 'keys-panel' },
          this.keyL,
          this.keyHint,
          this.keyR,
          showSpace ? this.keySpace : null,
        ),
      ),
    );
    const touch = h('div', { class: 'touch-controls', 'aria-label': 'Game controls' });
    const button = (label: string, key: RunKey | 'action') => {
      const el = h('button', { class: 'touch-key', 'aria-label': label }, label);
      const pointers = new Set<number>();
      el.addEventListener('pointerdown', (e) => {
        e.preventDefault();
        el.setPointerCapture(e.pointerId);
        if (pointers.size) return;
        pointers.add(e.pointerId);
        if (key === 'action') opts.onAction?.(true);
        else opts.onRun?.(key);
      });
      const release = (e: PointerEvent) => {
        if (!pointers.delete(e.pointerId)) return;
        if (key === 'action') opts.onAction?.(false);
      };
      el.addEventListener('pointerup', release);
      el.addEventListener('pointercancel', release);
      el.addEventListener('lostpointercapture', release);
      return el;
    };
    touch.append(button(keyLabel(opts.binding.left), 0), button(keyLabel(opts.binding.right), 1));
    if (showSpace) touch.append(button('Action', 'action'));
    this.root.append(touch, this.heightButton);
    parent.append(this.root);
  }

  setHeightChoice(height: number | null, step = 0.05): void {
    this.heightButton.hidden = height === null;
    if (height !== null) {
      this.heightButton.textContent = `Raise bar to ${(height + step).toFixed(2)} m`;
      this.nextHeight = height + step;
    }
  }

  setViewName(n: string): void {
    this.viewBtn.textContent = `🎥 ${n}`;
  }
  setMusicName(n: string): void {
    this.musicBtn.textContent = `🎵 ${n}`;
  }
  setSfx(on: boolean): void {
    this.sfxBtn.textContent = on ? '🔊 On' : '🔇 Off';
  }

  destroy(): void {
    this.root.remove();
  }

  /** Flash a key to show it was pressed; `counted` false shows the same-key rejection. */
  flashKey(key: RunKey | 'space', counted: boolean): void {
    const el = key === 'space' ? this.keySpace : key === 0 ? this.keyL : this.keyR;
    el.classList.remove('hit', 'dud');
    void el.offsetWidth;
    el.classList.add(counted ? 'hit' : 'dud');
  }

  /** Small banner under the stats for whose turn it is, the bar height and the like. */
  banner(text: string | null, cls = ''): void {
    this.bannerEl.hidden = !text;
    this.bannerEl.className = `hud-banner ${cls}`;
    this.bannerEl.textContent = text ?? '';
  }

  /** Big centred message (start sequence, false start, marks). */
  message(text: string | null, cls = ''): void {
    this.msgEl?.remove();
    this.msgEl = null;
    if (!text) return;
    this.msgEl = h('div', { class: 'countdown' }, h('div', { class: `num ${cls}` }, text));
    this.root.append(this.msgEl);
  }

  /** The athletes board: one line per athlete, kept in the given order. */
  setBoard(rows: HudBoardRow[]): void {
    this.boardEl.hidden = rows.length < 2;
    const seen = new Set<string>();
    rows.forEach((r, i) => {
      seen.add(r.id);
      let e = this.boardRows.get(r.id);
      if (!e) {
        const value = h('span', { class: 'ab-value mono' });
        const row = h(
          'div',
          { class: `ab-row${r.me ? ' me' : ''}` },
          h('span', { class: 'ab-dot', style: `background:${r.kit}` }),
          h('span', { class: 'ab-name' }, r.name),
          value,
        );
        e = { row, value };
        this.boardRows.set(r.id, e);
        this.boardEl.append(row);
      }
      if (e.value.textContent !== r.value) e.value.textContent = r.value;
      if (this.boardEl.children[i] !== e.row)
        this.boardEl.insertBefore(e.row, this.boardEl.children[i] ?? null);
    });
    for (const [id, e] of this.boardRows) {
      if (!seen.has(id)) {
        e.row.remove();
        this.boardRows.delete(id);
      }
    }
  }

  update(m: HudModel): void {
    this.primaryEl.textContent = m.primary;
    this.primaryLabel.textContent = m.primaryLabel;
    this.secondaryEl.textContent = m.secondary;
    this.secondaryLabel.textContent = m.secondaryLabel;
    this.speedEl.textContent = m.speed.toFixed(1);
    this.cadEl.textContent = m.cadence.toFixed(1);
    const pct = Math.min(100, (m.cadence / MAX_CADENCE) * 100);
    this.meterFill.style.width = `${pct}%`;
    const lo = ((m.sweetCadence - 1) / MAX_CADENCE) * 100;
    const hi = ((m.sweetCadence + 2) / MAX_CADENCE) * 100;
    this.meterBand.style.left = `${lo}%`;
    this.meterBand.style.width = `${hi - lo}%`;
    this.meterFill.classList.toggle('sweet', m.cadence >= m.sweetCadence - 1);
    this.staminaWrap.hidden = m.stamina === null;
    if (m.stamina !== null) {
      this.staminaFill.style.width = `${Math.round(m.stamina * 100)}%`;
      this.staminaFill.classList.toggle('low', m.stamina < 0.25);
    }
    this.keyL.classList.toggle('next', m.nextKey === 0);
    this.keyR.classList.toggle('next', m.nextKey === 1);
    clear(this.keyHint);
    this.keyHint.append(m.nextKey === -1 ? 'Alternate!' : '⟷');

    // Angle gauge: reset to empty whenever it hides so the next attempt starts from zero.
    this.gaugeWrap.hidden = m.gauge === null;
    if (m.gauge === null) {
      this.gaugeFill.style.height = '0%';
      this.gaugeText.textContent = '0°';
    } else {
      this.gaugeFill.style.height = `${(m.gauge / 90) * 100}%`;
      this.gaugeBand.style.bottom = `${((m.gaugeIdeal - 4) / 90) * 100}%`;
      this.gaugeBand.style.height = `${(8 / 90) * 100}%`;
      this.gaugeText.textContent = `${Math.round(m.gauge)}°`;
      this.gaugeFill.classList.toggle('sweet', Math.abs(m.gauge - m.gaugeIdeal) <= 4);
    }

    // Attempts
    if (m.attempts) {
      const key = m.attempts.map((a) => `${a.label}:${a.state}`).join('|');
      if (key !== this.lastAttempts) {
        this.lastAttempts = key;
        clear(this.attemptsEl);
        this.attemptsEl.hidden = false;
        this.attemptsEl.append(
          h(
            'div',
            { class: 'attempt-row' },
            m.attempts.map((a) => h('span', { class: `attempt ${a.state}` }, a.label)),
          ),
          h('div', { class: 'l' }, 'Attempts'),
        );
      }
    } else this.attemptsEl.hidden = true;
  }
}
